import {
  DRAWING_TYPE_LABELS,
  formatPrice,
  type AlertOutcome,
  type Drawing,
} from '@stockstop/shared';

/**
 * Der Stand des verbundenen Paares, über der Indikatorliste.
 *
 * Zeigt den Anteil am festgehaltenen Abstand — die 100 % —, die erreichte Stufe
 * und die eine Handlung, die daraus folgt: den Stop-Loss nachziehen.
 *
 * Steht hier und nicht in der Zeile des Indikators, weil die Zahl dem **Paar**
 * gilt. In der Liste stünde sie an einer von zwei Stellen, an denen sie nur halb
 * hingehört.
 *
 * Die Farbe folgt der Richtung, wie in der E-Mail: Rot, wenn der Indikator auf den
 * Stop zuläuft, Blau, wenn er sich entfernt. Wer beides kennt, sieht am Farbton
 * schon, um welche Art von Handlung es geht, bevor er das Wort gelesen hat.
 */

export interface LinkStateProps {
  /** Der verbundene Indikator. */
  drawing: Drawing;
  /** Das Ergebnis derselben Rechnung, die auch der Nachtlauf anstellt. */
  outcome: AlertOutcome;
  /** Wohin das Nachziehen die Stop-Loss-Linie setzen würde. */
  target: number | null;
  currency: string | null;
  onSync: () => void;
  isPending: boolean;
  /** Nach erfolgreichem Nachziehen: der neue Kurs, falls die Order veraltet ist. */
  outdatedBookingPrice: number | null;
  errorMessage: string | null;
}

const prozent = (wert: number): string =>
  `${wert.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;

export const LinkState = ({
  drawing,
  outcome,
  target,
  currency,
  onSync,
  isPending,
  outdatedBookingPrice,
  errorMessage,
}: LinkStateProps): React.JSX.Element => {
  const name = drawing.label ?? DRAWING_TYPE_LABELS[drawing.payload.type];

  return (
    <div
      className={`link-state${
        outcome.status === 'auffällig' ? ` link-state--${outcome.level.direction}` : ''
      }`}
    >
      <div className="link-state__kopf">
        <span className="link-state__anteil">
          {outcome.status === 'nicht-auswertbar' ? '—' : prozent(outcome.percent)}
        </span>
        {outcome.status === 'auffällig' ? (
          <span className="link-state__stufe">{outcome.level.label}</span>
        ) : (
          <span className="muted small">
            {outcome.status === 'unauffällig' ? 'im Rahmen' : outcome.reason}
          </span>
        )}
      </div>

      <p className="muted small link-state__zeile">
        {name} ist mit der Stop-Loss-Linie verbunden.
        {target !== null ? (
          <>
            {' '}
            Nachziehen setzt sie auf <strong>{formatPrice(target, { currency })}</strong>.
          </>
        ) : null}
      </p>

      <button
        type="button"
        onClick={onSync}
        disabled={target === null || isPending}
        title={
          target === null
            ? 'Ohne eingebuchten Stop gibt es keinen Bezugsabstand, auf den sich nachziehen ließe'
            : 'Setzt die Stop-Loss-Linie auf den festgehaltenen Abstand zum Indikator'
        }
      >
        {isPending ? 'Wird nachgezogen …' : 'Stop-Loss nachziehen'}
      </button>

      {/*
        Nach dem Nachziehen liegt beim Broker noch die alte Order. Das ist der
        nächste Schritt, und er gehört einem Menschen — hier wandert nur die
        Absicht.
      */}
      {outdatedBookingPrice !== null ? (
        <p className="hint hint--warn small link-state__hinweis">
          Die Linie steht jetzt bei {formatPrice(outdatedBookingPrice, { currency })}. Beim Broker
          liegt noch die alte Order — anpassen und neu einbuchen.
        </p>
      ) : null}

      {errorMessage !== null ? (
        <p className="hint hint--warn small link-state__hinweis">{errorMessage}</p>
      ) : null}
    </div>
  );
};
